import {OAuthEvent, OAuthService, UserInfo} from "angular-oauth2-oidc";
import {Injectable} from "@angular/core";
import {BehaviorSubject, combineLatest, concatMap, map, Observable, of} from "rxjs";
import {Router} from "@angular/router";
import {INavData} from "@coreui/angular";
import {UserProfile} from "../_models";
import {AuthConfig} from 'angular-oauth2-oidc';

// export const authConfig: AuthConfig = {
//   clientId: 'fitrite365_spa',
//   issuer: 'https://auth.fitrite.info/',
//   redirectUri: window.location.origin + '/index.html',
//   responseType: 'code',
//   scope: 'openid roles email server_scope fitrite365_api_scope deckit_api_scope'
//
// };

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private isAuthenticatedSubject$ = new BehaviorSubject<boolean>(false);
  public isAuthenticated$ = this.isAuthenticatedSubject$.asObservable();

  private isDoneLoadingSubject$ = new BehaviorSubject<boolean>(false);
  public isDoneLoading$ = this.isDoneLoadingSubject$.asObservable();

  private userProfileSubject$ = new BehaviorSubject<UserProfile>({
    username: AuthService.ANONYMOUS,
    roles: [],
    permissions: [],
    id: ''
  });
  public userProfile$ = this.userProfileSubject$.asObservable();

  public static ANONYMOUS = 'anonymous';

  public canActivateProtectedRoutes$: Observable<boolean> = combineLatest([
    this.isAuthenticated$,
    this.isDoneLoading$
  ]).pipe(map(values => values.every(b => b)));

  public hasApplicationAccess(): Observable<Boolean> {
    return this.canActivateProtectedRoutes$.pipe(concatMap(authed => {
        if (!authed) {
          return of(false);
        }

        return this.userProfileSubject$.pipe(map(userProfile => {
          return this.isAdmin || userProfile.permissions.some(x => x.startsWith('fitrite365.'))
        }));
      }
    ))
  }

  public get isAdmin(): boolean {
    return this.isInRole("Administrator");
  }

  public constructor(
    private oauth: OAuthService,
    private router: Router
  ) {
    console.log('AuthService init', {oauth, router});

    // this.oauth.setStorage(sessionStorage);
    // this.oauth.configure(authConfig);

    this.oauth.events.subscribe(async (e: OAuthEvent) => {
      if (e.type === 'token_received' || e.type === 'token_refreshed') {
        await this.loadProfile();
      }

      if (e.type === 'discovery_document_loaded' && this.oauth.hasValidAccessToken()) {
        await this.loadProfile();
      }

      if (e.type === 'user_profile_load_error') {
        this.oauth.logOut(false);
        await this.reset();
        await this.router.navigate(['/']);
      }

      if (e.type === 'user_profile_loaded') {
        this.isAuthenticatedSubject$.next(this.oauth.hasValidAccessToken());

        this.hasApplicationAccess().pipe(map(async applicationAccess => {
          if (!applicationAccess) {
            await this.router.navigate(['forbidden'])
          } else {
            let requestedUrl = sessionStorage.getItem('requestedUrl');
            if (requestedUrl) {
              sessionStorage.removeItem('requestedUrl');
              await this.router.navigate([requestedUrl])
            }
          }
        }))
      }
    });
  }

  public async reset(): Promise<void> {
    await this.loadProfile();
  }

  public isInRole(role: string): boolean {
    let userProfile = this.userProfileSubject$.value;
    if (!userProfile.roles || !role) {
      return false;
    }

    return userProfile.roles.some(r => r === role);
  }

  public async loadProfile(): Promise<UserInfo> {
    if (this.oauth.hasValidAccessToken()) {
      try {
        const info: any = await this.oauth.loadUserProfile();

        // 👇 some servers return user claims under info.info
        const claims = info?.info ?? info;

        const username = claims.name || claims.email;
        const roles = Array.isArray(claims.role)
          ? claims.role
          : claims.role ? [claims.role] : [];
        const permissions = Array.isArray(claims.permissions)
          ? claims.permissions
          : [];

        const id = info.id;

        const userProfile = {
          username,
          roles,
          permissions,
          id
        };

        // console.log('✅ User profile loaded:', userProfile);

        this.userProfileSubject$.next(userProfile);
        this.isAuthenticatedSubject$.next(this.oauth.hasValidAccessToken());
        this.isDoneLoadingSubject$.next(true);

        return info;
      } catch (err) {
        console.error('❌ Failed to load user profile', err);
      }
    }

    return {} as UserInfo;
  }


  hasPermission(permission: string): boolean {
    let permissions = this.userProfileSubject$.value.permissions;
    if (!permissions || !permission) {
      return false;
    }

    return permissions.some((r => r === permission));
  }

  filterNavDataArray(navData: INavData[]): INavData[] {
    if (!this.isAdmin && !this.userProfileSubject$.value.permissions.some(x => x.startsWith('fitrite365.'))) {
      return [];
    }

    if (this.isAdmin) {
      return navData;
    }
    return this.noNulls(navData.map(this.filterNavData, this));
  }

  private filterNavData(navData: INavData): INavData | null {
    const permissions: string[] | null = navData.attributes && navData.attributes['permissions'] || null;
    if (permissions && !permissions.some(this.hasPermission, this)) {
      return null;
    }

    if (navData.children) {
      navData.children = this.filterNavDataArray(navData.children);
    }
    return navData;
  }

  private noNulls<T>(items: (T | null)[]) {
    return items.filter((x): x is T => x !== null);
  }

  async initAuth(): Promise<void> {
    await this.oauth.loadDiscoveryDocument();

    // Try login, but DO NOT rely on access token validity
    await this.oauth.tryLogin({
      onTokenReceived: () => {
        console.log('🔑 Token received');
        this.loadProfile();
      }
    });

    // Only check ID token, because access token is a reference token
    if (!this.oauth.hasValidIdToken()) {
      this.oauth.initCodeFlow();
      return;
    }

    console.log('✅ Logged in as', this.oauth.getIdentityClaims());
    await this.loadProfile();
  }


  getAccessToken(): string | null {
    return this.oauth.getAccessToken();
  }

  public logOut() {
    this.oauth.logOut(false);
    this.reset();
  }

  public login() {
    if(!this.oauth.hasValidAccessToken()) {
      this.oauth.initCodeFlow();
    }
  }

  // public setStorage() {
  //   this.oauth.setStorage(sessionStorage);
  // }

  public signalAuthInitComplete(): void {
    if (!this.oauth.hasValidIdToken()) {
      // Not logged in — mark as done so guards can redirect to login
      this.isDoneLoadingSubject$.next(true);
    }
  }


}

